import React from 'react';
import {
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip
} from '@mui/material';

export type StatusFilter = 'all' | 'no-bid' | 'pending' | 'accepted' | 'ignored';

interface StatusCounts {
  all: number;
  'no-bid': number;
  pending: number;
  accepted: number;
  ignored: number;
}

interface ServiceRequestFiltersProps {
  statusFilter: StatusFilter;
  setStatusFilter: (filter: StatusFilter) => void;
  serviceTypeFilter: string;
  setServiceTypeFilter: (filter: string) => void;
  statusCounts: StatusCounts;
  serviceTypes: string[];
}

const ServiceRequestFilters: React.FC<ServiceRequestFiltersProps> = ({
  statusFilter,
  setStatusFilter,
  serviceTypeFilter,
  setServiceTypeFilter,
  statusCounts,
  serviceTypes
}) => {
  return (
    <Box sx={{ 
      mb: 3,
      p: 3,
      bgcolor: 'white',
      borderRadius: '16px',
      border: '1px solid rgba(16, 29, 59, 0.2)',
      boxShadow: '0px 4px 16px -4px rgba(16, 29, 59, 0.08)'
    }}>
      <Typography 
        variant="h6" 
        sx={{ 
          fontWeight: 700, 
          color: '#101D3B',
          mb: 2 
        }}
      >
        Filter Options
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        {/* Universal filter dropdown for all platforms */}
        <FormControl 
          fullWidth
          variant="outlined" 
          size="small"
          sx={{ mb: 2 }}
        >
          <InputLabel id="status-filter-label">Filter by Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            label="Filter by Status"
            sx={{ 
              borderRadius: '16px',
              border: '1px solid rgba(16, 29, 59, 0.2)',
              '.MuiSelect-select': {
                padding: { xs: '12px 14px', sm: '12px 16px' },
                fontSize: { xs: '14px', sm: '16px' }
              },
              '&:hover': {
                borderColor: 'rgba(16, 29, 59, 0.3)',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#00AB9B',
                borderWidth: '1px'
              }
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 350,
                  width: '100%',
                  borderRadius: '16px',
                  mt: 0.5,
                  boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
                  border: '1px solid rgba(16, 29, 59, 0.2)'
                },
              },
            }}
          >
            <MenuItem value="all" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: statusFilter === 'all' ? 700 : 400 }}>All Requests</Typography>
                <Chip 
                  label={statusCounts.all} 
                  color="primary" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: '24px', 
                    minWidth: '24px',
                    bgcolor: statusFilter === 'all' ? '#00AB9B' : undefined,
                    '& .MuiChip-label': {
                      px: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="no-bid" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: statusFilter === 'no-bid' ? 700 : 400 }}>No Bid</Typography>
                <Chip 
                  label={statusCounts['no-bid']} 
                  color="default" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: '24px', 
                    minWidth: '24px',
                    bgcolor: statusFilter === 'no-bid' ? 'rgba(16, 29, 59, 0.1)' : undefined,
                    '& .MuiChip-label': {
                      px: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="pending" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: statusFilter === 'pending' ? 700 : 400 }}>Sent</Typography>
                <Chip 
                  label={statusCounts.pending} 
                  color="warning" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: '24px', 
                    minWidth: '24px',
                    '& .MuiChip-label': {
                      px: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="accepted" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                <Typography sx={{ fontWeight: statusFilter === 'accepted' ? 700 : 400 }}>Won</Typography>
                <Chip 
                  label={statusCounts.accepted} 
                  color="success" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    height: '24px', 
                    minWidth: '24px',
                    '& .MuiChip-label': {
                      px: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </MenuItem>
            <MenuItem value="ignored" sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Typography sx={{ fontWeight: statusFilter === 'ignored' ? 700 : 400 }}>Ignored</Typography>
                <Chip 
                  size="small" 
                  label={statusCounts.ignored}
                  color="error" 
                  sx={{ 
                    ml: 1, 
                    height: '24px', 
                    minWidth: '24px',
                    '& .MuiChip-label': {
                      px: 1,
                      fontWeight: 500
                    }
                  }}
                />
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>
      
      <FormControl 
        variant="outlined" 
        size="small" 
        sx={{ 
          width: '100%',
          maxWidth: '100%',
          mb: 2
        }}
      >
        <InputLabel id="service-type-filter-label">Service Type</InputLabel>
        <Select
          labelId="service-type-filter-label"
          value={serviceTypeFilter}
          onChange={(e) => setServiceTypeFilter(e.target.value)}
          label="Service Type"
          sx={{ 
            borderRadius: '16px',
            border: '1px solid rgba(16, 29, 59, 0.2)',
            '.MuiSelect-select': {
              padding: { xs: '12px 14px', sm: '12px 16px' },
              fontSize: { xs: '14px', sm: '16px' }
            },
            '&:hover': {
              borderColor: 'rgba(16, 29, 59, 0.3)',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#00AB9B',
              borderWidth: '1px'
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 350,
                width: '100%',
                borderRadius: '16px',
                mt: 0.5,
                boxShadow: '0px 8px 32px -4px rgba(16, 29, 59, 0.1)',
                border: '1px solid rgba(16, 29, 59, 0.2)'
              },
            },
          }}
        >
          {serviceTypes.map(type => (
            <MenuItem 
              key={type} 
              value={type} 
              sx={{ 
                fontSize: { xs: '14px', sm: '16px' },
                py: 1.5,
                fontWeight: serviceTypeFilter === type ? 700 : 400
              }}
            >
              {type === 'all' ? 'All Service Types' : type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default ServiceRequestFilters; 